import { useNavigate, useParams } from 'react-router'
import 'survey-core/defaultV2.min.css'
import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import Loader from '../../common/Loader'
import { getMe } from '../../MeProvider.ts'
import { getAssessmentResponseByUserId } from './utils.ts'
import { useCompleteAssessmentResponse, useGetAssessment, useSaveAnswers } from '../../http/hooks.ts'
import { Page, PageProps } from '../../layout/Page.tsx'
import SurveyBuilder, { SurveyAnswer, SurveyJson } from '../../surveyControl/SurveyBuilder.tsx'
import { mapAssessmentSurveyData } from '../../surveyControl/mappers/assessments.tsx'
import { useNextPeakClient } from '../../services/nextpeakClient/clientProvider.tsx'

interface AssessmentSurveyProps {
	assessmentId: string
	responseId: string
	data: SurveyJson
}

const SurveyLayer = ({ data, responseId, assessmentId }: AssessmentSurveyProps) => {
	const queryClient = useQueryClient()
	const client = useNextPeakClient()
	const mutation = useSaveAnswers(client)
	const completeMutation = useCompleteAssessmentResponse(client)
	const navigation = useNavigate()
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

	const surveyComplete = useCallback(async (answers: SurveyAnswer[]) => {
		try {
			setIsSubmitting(true)
			const payload = {
				answers: answers.map(({ id, value }) => ({
					questionId: id,
					answerValue: Number(value),
				})),
			}
			console.log(payload)
			await mutation.mutateAsync({ assessmentResponseId: responseId, answers: payload })
			await completeMutation.mutateAsync(responseId)
			await queryClient.invalidateQueries({ queryKey: ['UserAssessments'] })
			navigation(`/assessments/${assessmentId}`)
		} catch (e) {
			console.error(e)
		} finally {
			setIsSubmitting(false)
		}
	}, [])

	if (isSubmitting) {
		return <Loader />
	}

	return <SurveyBuilder surveyJson={data} onSubmit={surveyComplete} />
}

const AssessmentSurvey = () => {
	const { assessmentId, responseId } = useParams()
	const client = useNextPeakClient()

	const pageProps: PageProps = {
		title: 'Assessments',
		breadcrumbs: 'Assessments',
	}

	if (!assessmentId || !responseId || assessmentId === 'undefined' || responseId === 'undefined') {
		return (
			<Page {...pageProps}>
				<div className="flex flex-col gap-10">
					<Loader />
				</div>
			</Page>
		)
	}

	const { id } = getMe()
	const { data } = useGetAssessment(client, {
		assessmentId,
		includes: ['assessmentType', 'assessmentType.questions', 'assessmentResponses', 'employee'],
	})

	if (!data) {
		return (
			<Page {...pageProps}>
				<div className="flex flex-col gap-10">
					<Loader />
				</div>
			</Page>
		)
	}

	const myResponse = getAssessmentResponseByUserId(data, id!)

	if (!myResponse) {
		return (
			<Page {...pageProps}>
				<div className="flex flex-col gap-10">
					<div>Not allowed to be here</div>
				</div>
			</Page>
		)
	}

	if (myResponse.completedAt) {
		return (
			<Page {...pageProps}>
				<div className="flex flex-col gap-10">
					<div>You have already answered this assessment</div>
				</div>
			</Page>
		)
	}

	return (
		<Page {...pageProps}>
			<div className="flex flex-col gap-10">
				<SurveyLayer
					data={mapAssessmentSurveyData(data.assessmentType!.questions!, data.employee!, getMe())}
					assessmentId={assessmentId}
					responseId={responseId!}
				/>
			</div>
		</Page>
	)
}

export default AssessmentSurvey
